import { useState, useEffect } from 'react';
import Papa from 'papaparse';
import { Specialization } from '../types';

export const useSpecializations = () => {
  const [specializations, setSpecializations] = useState<Specialization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSpecializations = async () => {
      try {
        const response = await fetch('/specializations.csv');
        const csvText = await response.text();
        
        Papa.parse(csvText, {
          header: true,
          complete: (results) => {
            try {
              const specs: Specialization[] = results.data
                .filter((row: any) => row.name && row.name.trim() !== '')
                .map((row: any) => ({
                  name: row.name,
                  general_category: row.general_category as Specialization['general_category'],
                  specific_category: row.specific_category,
                  all_levels: row.all_levels
                    .replace(/"/g, '')
                    .split(',')
                    .map((level: string) => parseInt(level.trim()))
                    .filter((level: number) => !isNaN(level)),
                  description: row.description,
                  source_sections: row.source_sections,
                }));
              
              setSpecializations(specs);
              setLoading(false);
            } catch (err) {
              setError('Failed to parse CSV data');
              setLoading(false);
            }
          },
          error: (err: any) => {
            setError('Failed to parse CSV: ' + err.message);
            setLoading(false);
          }
        });
      } catch (err) {
        setError('Failed to load specializations');
        setLoading(false);
      }
    };

    loadSpecializations();
  }, []);

  // Helper function to get specializations available at a specific level
  const getSpecializationsForLevel = (level: number): Specialization[] => {
    return specializations.filter(spec => spec.all_levels.includes(level));
  };

  // Helper function to get specializations by category for a level
  const getSpecializationsByCategory = (level: number) => {
    const specs = getSpecializationsForLevel(level);
    const byCategory: { [key: string]: Specialization[] } = {};
    
    specs.forEach(spec => {
      if (!byCategory[spec.general_category]) {
        byCategory[spec.general_category] = [];
      }
      byCategory[spec.general_category].push(spec);
    });
    
    return byCategory;
  };

  return {
    specializations,
    loading,
    error,
    getSpecializationsForLevel,
    getSpecializationsByCategory,
  };
};